import React from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight, ClipboardList, LayoutGrid, Timer, HardHat,
  Wrench, Eye, ShieldCheck, ChevronRight,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Reveal, Stagger, StaggerItem, AnimatedCounter } from "../components/Reveal";
import EditorialBanners from "../components/EditorialBanners";

const ICONS = { ClipboardList, LayoutGrid, Timer, HardHat, Wrench, Eye, ShieldCheck };

const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, "") || "";
const API_BASE = BACKEND_BASE ? `${BACKEND_BASE}/api` : "/api";

const getWhatsappUrl = (whatsappRaw, msg = "Olá, gostaria de uma avaliação técnica.") =>
  `https://wa.me/${whatsappRaw}?text=${encodeURIComponent(msg)}`;

const fetchHomeContent = async () => {
  const { data } = await axios.get(`${API_BASE}/home`);
  return data;
};

const useHomeContent = () =>
  useQuery(["homeContent"], fetchHomeContent, {
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

function Hero({ images }) {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.5], [0, -120]);
  const opacity = useTransform(scrollYProgress, [0, 0.25], [1, 0]);

  return (
    <section className="relative h-[100svh] w-full overflow-hidden bg-sca-black text-white" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={images.heroPrimary || ""}
          alt="Obra industrial — guindaste contra céu"
          className="absolute inset-0 w-full h-[115%] object-cover"
          draggable={false}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/90" />
        <div className="absolute inset-0 sca-grid-lines opacity-30" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative h-full sca-container flex flex-col justify-end pb-20 md:pb-28 z-10">
        <Reveal delay={0.1} y={20}>
          <p className="sca-overline text-white/70 mb-6">
            <span className="inline-block w-8 h-px bg-sca-red align-middle mr-3" />
            [ Construtora · Piracicaba/SP · desde 2013 ]
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-9">
            <Reveal y={40} duration={1.1}>
              <h1 className="font-sans text-[40px] sm:text-5xl md:text-6xl lg:text-[72px] xl:text-[84px] font-extrabold leading-[0.98] tracking-tightest text-white max-w-[20ch]">
                Construtora especializada em <span className="sca-serif font-medium text-sca-red">obras industriais</span> e comerciais com planejamento, controle e execução técnica.
              </h1>
            </Reveal>
          </div>

          <div className="lg:col-span-3">
            <Reveal delay={0.3} y={20}>
              <p className="text-white/75 text-base md:text-lg leading-relaxed mb-7 max-w-sm">
                A SCA Construtora atua com engenharia aplicada, organização de obra e execução rigorosa
                para garantir previsibilidade, segurança e qualidade.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/contato" className="btn-sca-primary" data-testid="hero-cta-primary">
                  Avaliação técnica <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/metodologia-de-obra" className="btn-sca px-7 py-4 border border-white/30 text-white hover:bg-white hover:text-sca-black" data-testid="hero-cta-secondary">
                  Metodologia
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function ProblemSolution() {
  return (
    <section className="sca-section bg-white" data-testid="problem-solution-section">
      <div className="sca-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
        <Reveal className="lg:col-span-5 lg:sticky lg:top-32">
          <p className="sca-overline mb-6">[ Diagnóstico ]</p>
          <h2 className="sca-h2">
            O problema da maioria das obras não está na construção. Está na <span className="sca-serif text-sca-red">falta de organização</span>.
          </h2>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <p className="sca-body mb-6">
            Grande parte dos atrasos, desperdícios e retrabalhos na construção civil acontece por falta
            de planejamento técnico, controle operacional e gestão organizada da execução.
          </p>
          <p className="sca-body mb-10">
            Sem método, a obra depende de decisões improvisadas, compras emergenciais e soluções reativas.
            O resultado é previsível: cronogramas comprometidos, custos descontrolados, retrabalho e
            perda de produtividade.
          </p>

          <div className="border-t border-sca-line pt-10">
            <p className="sca-overline mb-6">[ Solução SCA ]</p>
            <h3 className="font-sans text-2xl md:text-3xl font-semibold text-sca-black leading-snug mb-5">
              A SCA estrutura a obra para que ela funcione do início à entrega.
            </h3>
            <p className="sca-body">
              Atuamos com metodologia organizada de engenharia para garantir controle técnico, segurança
              operacional e previsibilidade em todas as etapas. Mais do que executar, organizamos cada
              processo para reduzir riscos, evitar improvisos e garantir alinhamento entre planejamento,
              execução e entrega.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function StatsBand({ stats }) {
  return (
    <section className="relative bg-sca-black text-white py-24 md:py-32 overflow-hidden" data-testid="stats-section">
      <img src={images.rebar || ""} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-br from-black/95 via-black/85 to-black/95" />
      <div className="absolute inset-0 sca-grid-lines opacity-25" />

      <div className="sca-container relative">
        <Reveal>
          <div className="flex items-center justify-between flex-wrap gap-6 mb-14">
            <div>
              <p className="sca-overline text-white/55 mb-4">[ A SCA em números ]</p>
              <h2 className="font-sans text-3xl md:text-5xl font-bold tracking-tightest leading-tight max-w-3xl">
                Engenharia que se mede em <span className="sca-serif text-sca-red">resultado entregue</span>.
              </h2>
            </div>
          </div>
        </Reveal>

        <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-sca-lineDark border-t border-b border-sca-lineDark">
          {stats.map((s, i) => (
            <StaggerItem key={i} className="bg-sca-black p-8 md:p-10 hover:bg-sca-graphite transition-colors duration-500">
              <div className="font-sans text-5xl md:text-6xl xl:text-7xl font-extrabold tracking-tightest leading-none text-white">
                <AnimatedCounter to={s.value} suffix={s.suffix} />
              </div>
              <p className="mt-4 text-[12px] tracking-[0.22em] uppercase font-semibold text-white/65 max-w-[18ch]">
                {s.label}
              </p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function ServicesGrid({ services }) {
  return (
    <section className="sca-section bg-sca-mist" data-testid="services-section">
      <div className="sca-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <Reveal className="lg:col-span-6">
            <p className="sca-overline mb-6">[ Serviços ]</p>
            <h2 className="sca-h2">
              Segmentos atendidos com <span className="sca-serif text-sca-red">método</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5 lg:col-start-8 flex items-end">
            <p className="sca-body">
              Cada segmento é tratado como um sistema de engenharia próprio — planejado, controlado e
              executado com rigor técnico. Conheça as áreas em que a SCA atua.
            </p>
          </Reveal>
        </div>

        <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-sca-line border-t border-l border-sca-line">
          {services.map((s) => (
            <StaggerItem key={s.slug}>
              <Link
                to={`/servicos/${s.slug}`}
                data-testid={`service-card-${s.slug}`}
                className="group relative block bg-white p-8 md:p-10 border-r border-b border-sca-line hover:bg-sca-black hover:text-white transition-colors duration-500 h-full"
              >
                <div className="aspect-[4/3] overflow-hidden mb-8 -mx-8 md:-mx-10 -mt-8 md:-mt-10">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out"
                    loading="lazy"
                  />
                </div>
                <div className="flex items-baseline justify-between mb-5">
                  <span className="font-mono text-xs tracking-[0.28em] text-sca-red font-bold">{s.number}</span>
                  <ArrowUpRight className="w-5 h-5 opacity-40 group-hover:opacity-100 group-hover:text-sca-red transition-all group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
                <h3 className="font-sans text-2xl md:text-3xl font-semibold tracking-tight leading-tight mb-4 group-hover:text-white">
                  {s.title}
                </h3>
                <p className="text-sm md:text-base leading-relaxed text-sca-steel group-hover:text-white/70 max-w-md">
                  {s.short}
                </p>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function MethodologyPreview({ methodology }) {
  return (
    <section className="sca-section bg-white" data-testid="methodology-section">
      <div className="sca-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <Reveal className="lg:col-span-7">
            <p className="sca-overline mb-6">[ Metodologia · 6 etapas ]</p>
            <h2 className="sca-h2">
              Como a execução funciona <span className="sca-serif text-sca-red">na prática</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4 lg:col-start-9 flex items-end">
            <Link to="/metodologia-de-obra" className="btn-sca-ghost" data-testid="methodology-cta">
              Conheça o método completo <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>

        {/* Horizontal scroll timeline */}
        <div className="relative">
          <div className="absolute left-0 right-0 top-[88px] h-px bg-sca-line" aria-hidden />
          <Stagger className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-sca-line border-t border-l border-sca-line">
            {methodology.map((step, i) => (
              <StaggerItem key={step.n}>
                <div className="bg-white p-7 md:p-8 border-r border-b border-sca-line h-full hover:bg-sca-black hover:text-white transition-colors duration-500 group">
                  <div className="flex items-center justify-between mb-7">
                    <span className="font-mono text-xs tracking-[0.28em] text-sca-red font-bold">{step.n}</span>
                    <span className="w-2 h-2 bg-sca-red group-hover:bg-white" />
                  </div>
                  <h3 className="font-sans text-base md:text-lg font-semibold mb-3 leading-tight">
                    {step.title}
                  </h3>
                  <p className="text-[13px] leading-relaxed text-sca-steel group-hover:text-white/70">
                    {step.desc}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </div>
    </section>
  );
}

function DifferentialsSection({ differentials }) {
  return (
    <section className="relative bg-sca-graphite text-white sca-section overflow-hidden" data-testid="differentials-section">
      <img src={images.metalStructure || ""} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-15" />
      <div className="absolute inset-0 bg-gradient-to-br from-sca-graphite via-sca-black to-sca-graphite" />

      <div className="sca-container relative">
        <Reveal>
          <p className="sca-overline text-white/55 mb-6">[ Diferenciais ]</p>
          <h2 className="font-sans text-3xl md:text-5xl lg:text-6xl font-bold tracking-tightest leading-[1.05] max-w-4xl mb-16 md:mb-20">
            O que muda quando existe <span className="sca-serif text-sca-red">organização e método</span>.
          </h2>
        </Reveal>

        <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-sca-lineDark border-t border-l border-sca-lineDark">
          {differentials.map((d) => {
            const Icon = ICONS[d.icon] || ClipboardList;
            return (
              <StaggerItem key={d.title}>
                <div className="bg-sca-graphite p-8 md:p-10 border-r border-b border-sca-lineDark h-full hover:bg-sca-black transition-colors duration-500 group">
                  <Icon className="w-9 h-9 text-sca-red mb-7 group-hover:scale-110 transition-transform duration-500" strokeWidth={1.5} />
                  <h3 className="font-sans text-lg md:text-xl font-semibold mb-3 leading-tight">{d.title}</h3>
                  <p className="text-[13px] md:text-sm leading-relaxed text-white/65">{d.desc}</p>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>
  );
}

function ProjectsPreview({ projects }) {
  const featured = projects.slice(0, 4);
  return (
    <section className="sca-section bg-white" data-testid="projects-preview-section">
      <div className="sca-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
          <Reveal className="lg:col-span-7">
            <p className="sca-overline mb-6">[ Projetos ]</p>
            <h2 className="sca-h2">
              Engenharia aplicada <span className="sca-serif text-sca-red">na prática</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4 lg:col-start-9 flex items-end">
            <Link to="/projetos" className="btn-sca-ghost" data-testid="projects-preview-cta">
              Ver portfólio completo <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>

        <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {featured.map((p, i) => (
            <StaggerItem key={p.id} className={i % 2 === 1 ? "md:translate-y-12" : ""}>
              <Link
                to={`/projetos/${p.category}`}
                data-testid={`project-preview-${p.id}`}
                className="group block"
              >
                <div className="aspect-[4/3] overflow-hidden bg-sca-mist">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1400ms] ease-out"
                    loading="lazy"
                  />
                </div>
                <div className="pt-5 flex items-start justify-between gap-6">
                  <div>
                    <p className="text-[11px] tracking-[0.24em] uppercase font-bold text-sca-red mb-2">
                      {p.year} · {p.location}
                    </p>
                    <h3 className="font-sans text-xl md:text-2xl font-semibold leading-tight max-w-md group-hover:text-sca-red transition-colors">
                      {p.title}
                    </h3>
                  </div>
                  <ChevronRight className="w-5 h-5 mt-1 text-sca-steel group-hover:text-sca-red group-hover:translate-x-1 transition-all" />
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function FinalCTA({ images, whatsappUrl }) {
  return (
    <section className="relative bg-sca-black text-white py-32 md:py-40 overflow-hidden" data-testid="final-cta-section">
      <img src={images.heroAlt || ""} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/80 to-black/70" />
      <div className="absolute inset-0 sca-grid-lines opacity-25" />

      <div className="sca-container relative">
        <Reveal>
          <p className="sca-overline text-white/60 mb-8">[ Próximo passo ]</p>
          <h2 className="font-sans text-4xl md:text-6xl lg:text-7xl font-extrabold leading-[1.02] tracking-tightest max-w-[18ch]">
            Obras complexas exigem <span className="sca-serif text-sca-red">controle, método e responsabilidade</span>.
          </h2>
          <p className="mt-10 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed">
            Solicite uma avaliação técnica e entenda como a SCA pode estruturar sua obra com organização,
            previsibilidade e segurança.
          </p>
          <div className="mt-12 flex flex-wrap gap-3">
            <a href={whatsappUrl()} target="_blank" rel="noreferrer" className="btn-sca-primary" data-testid="final-cta-whatsapp">
              Solicitar avaliação técnica <ArrowUpRight className="w-4 h-4" />
            </a>
            <Link to="/contato" className="btn-sca px-7 py-4 border border-white/25 text-white hover:bg-white hover:text-sca-black" data-testid="final-cta-form">
              Formulário detalhado
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  const { data = {}, isError } = useHomeContent();
  const images = data.images ?? {};
  const methodology = data.methodology ?? [];
  const services = data.services ?? [];
  const differentials = data.differentials ?? [];
  const stats = data.stats ?? [];
  const projects = data.projects ?? [];
  const company = data.company ?? {};
  const whatsappUrl = (msg) => getWhatsappUrl(company.whatsappRaw ?? "5519997476891", msg);

  if (isError) {
    return (
      <section className="sca-section bg-white py-24">
        <div className="sca-container">
          <p className="font-sans text-lg text-sca-red mb-4">
            Não foi possível carregar o conteúdo da página.
          </p>
          <p className="text-sca-black">
            Tente recarregar ou verifique sua conexão com a internet.
          </p>
        </div>
      </section>
    );
  }

  return (
    <>
      <Hero images={images} />
      <ProblemSolution />
      <EditorialBanners />
      <StatsBand stats={stats} />
      <ServicesGrid services={services} />
      <MethodologyPreview methodology={methodology} />
      <DifferentialsSection differentials={differentials} />
      <ProjectsPreview projects={projects} />
      <FinalCTA images={images} whatsappUrl={whatsappUrl} />
    </>
  );
}
